import {
  IndiciaReasonCode,
  TaxEntryType,
  TaxInfoEntry,
  TaxInfoRequestPayload,
} from '@/types/api/tax-info';

import { ForeignIndiciaCountryDetail } from '@/utils/user';

import {
  buildForeignNonTaxResidencyPayload,
  getForeignNonTaxResidencyEntries,
  mapTaxInfoEntryToForeignNonTaxResidencyBlock,
  shouldShowForeignNonTaxResidencyForm,
} from '../ForeignNonTaxResidencySection/utils';
import {
  buildForeignTaxResidencyPayload,
  getForeignTaxResidencyEntries,
  mapTaxInfoEntryToForeignTaxResidencyBlock,
  shouldShowForeignTaxResidencyForm,
} from '../ForeignTaxResidencySection/utils';
import { buildForeignIndiciaExplanationPayload } from '../OtherCountriesForeignIndiciaSection/utils';
import {
  QUESTION_2_TAX_ENTRY_TYPES,
  replaceOwnedTaxEntries,
} from '../utils/taxInfoStatus';
import { OtherCountriesTaxFormValues } from '../utils/types';

export const DEFAULT_OTHER_COUNTRIES_TAX_FORM_VALUES: OtherCountriesTaxFormValues =
  {
    hasForeignIndicia: undefined,
    isForeignTaxResident: undefined,
    indiciaReasonCode: {},
    indiciaReasonOther: {},
    foreignTaxResidencies: [],
    foreignNonTaxResidencies: [],
  };

export function mapTaxInfoToOtherCountriesFormValues(
  taxInfo?: TaxInfoEntry[],
): OtherCountriesTaxFormValues {
  if (!taxInfo?.length) {
    return DEFAULT_OTHER_COUNTRIES_TAX_FORM_VALUES;
  }

  const nonTaxResidencyEntries = getForeignNonTaxResidencyEntries(taxInfo);

  if (nonTaxResidencyEntries.length) {
    return {
      hasForeignIndicia: true,
      isForeignTaxResident: false,
      indiciaReasonCode: {},
      indiciaReasonOther: {},
      foreignTaxResidencies: [],
      foreignNonTaxResidencies: nonTaxResidencyEntries.map(
        mapTaxInfoEntryToForeignNonTaxResidencyBlock,
      ),
    };
  }

  const residencyEntries = getForeignTaxResidencyEntries(taxInfo);

  if (residencyEntries.length) {
    return {
      hasForeignIndicia: residencyEntries[0].hasResidenceInOtherCountry ?? true,
      isForeignTaxResident: true,
      indiciaReasonCode: {},
      indiciaReasonOther: {},
      foreignTaxResidencies: residencyEntries.map(
        mapTaxInfoEntryToForeignTaxResidencyBlock,
      ),
      foreignNonTaxResidencies: [],
    };
  }

  const indiciaEntries = taxInfo.filter(
    entry => entry.taxEntryType === TaxEntryType.FOREIGN_INDICIA_EXPLANATION,
  );

  if (indiciaEntries.length) {
    const indiciaReasonCode: Record<string, string> = {};
    const indiciaReasonOther: Record<string, string> = {};

    indiciaEntries.forEach(entry => {
      if (!entry.countryCode) {
        return;
      }

      indiciaReasonCode[entry.countryCode] = entry.indiciaReasonCode ?? '';

      if (entry.indiciaReasonCode === IndiciaReasonCode.OTHER) {
        indiciaReasonOther[entry.countryCode] = entry.indiciaReasonOther ?? '';
      }
    });

    return {
      hasForeignIndicia: false,
      isForeignTaxResident: false,
      indiciaReasonCode,
      indiciaReasonOther,
      foreignTaxResidencies: [],
      foreignNonTaxResidencies: [],
    };
  }

  if (taxInfo.some(entry => entry.taxEntryType === TaxEntryType.NO_TAX)) {
    return {
      hasForeignIndicia: false,
      isForeignTaxResident: false,
      indiciaReasonCode: {},
      indiciaReasonOther: {},
      foreignTaxResidencies: [],
      foreignNonTaxResidencies: [],
    };
  }

  return DEFAULT_OTHER_COUNTRIES_TAX_FORM_VALUES;
}

export function mapNoTaxEntryToFormValues(
  entry?: TaxInfoEntry,
): OtherCountriesTaxFormValues {
  if (!entry) {
    return DEFAULT_OTHER_COUNTRIES_TAX_FORM_VALUES;
  }

  return {
    hasForeignIndicia: false,
    isForeignTaxResident: false,
    indiciaReasonCode: {},
    indiciaReasonOther: {},
  };
}

export function shouldSaveNoForeignTaxEntry(
  formValues: OtherCountriesTaxFormValues,
): boolean {
  return (
    formValues.hasForeignIndicia === false &&
    formValues.isForeignTaxResident === false
  );
}

export function shouldShowForeignIndiciaWarning(
  formValues: OtherCountriesTaxFormValues,
  hasProfileForeignIndicia: boolean,
): boolean {
  return shouldSaveNoForeignTaxEntry(formValues) && hasProfileForeignIndicia;
}

export function buildOtherCountriesTaxStatusPayload(
  formValues: OtherCountriesTaxFormValues,
  countryCode: string,
  foreignIndiciaCountries: ForeignIndiciaCountryDetail[],
  existingTaxInfo?: TaxInfoEntry[],
  hasProfileForeignIndicia = false,
): TaxInfoRequestPayload {
  if (shouldShowForeignTaxResidencyForm(formValues)) {
    return buildForeignTaxResidencyPayload(formValues, existingTaxInfo);
  }

  if (shouldShowForeignNonTaxResidencyForm(formValues)) {
    return buildForeignNonTaxResidencyPayload(formValues, existingTaxInfo);
  }

  if (shouldShowForeignIndiciaWarning(formValues, hasProfileForeignIndicia)) {
    return buildForeignIndiciaExplanationPayload(
      formValues,
      foreignIndiciaCountries,
      existingTaxInfo,
    );
  }
  return buildNoTaxRequestPayload(countryCode, existingTaxInfo);
}

export function buildNoTaxRequestPayload(
  countryCode: string,
  existingTaxInfo?: TaxInfoEntry[],
): TaxInfoRequestPayload {
  const noTaxEntry: TaxInfoEntry = {
    countryCode,
    taxEntryType: TaxEntryType.NO_TAX,
    isCompleted: true,
  };

  return replaceOwnedTaxEntries(
    existingTaxInfo ?? [],
    [noTaxEntry],
    [...QUESTION_2_TAX_ENTRY_TYPES],
  );
}
